import * as React from 'react';
import { CopyableField } from './CopyableField/CopyableField';
import Link from '@app/react-common/components/link/Link';
import { AbTestFormData } from '../AbTestModal';
import {
  EXTENSIONS_AB_TEST_ID_PREFIX,
  ExtensionsGroupType,
  buildExtensionsGroupUsageToken,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IExtensionGroup } from '../../../../../extension-groups/models/extension-group.models';
import { AbTestWidgetType } from '../../../models/AbTestCreationType';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';

const INSTALLATION_INSTRUCTIONS_URL =
  'https://support.prettydamnquick.com/en/articles/8560867-adding-multiple-pdq-components';
const instructionsLinkStyle: React.CSSProperties = { textDecoration: 'none' };

const InstallationInstructionsLink: React.FC = () => (
  <div className="ab-test-modal__tokens__instructions-link">
    <Link
      url={INSTALLATION_INSTRUCTIONS_URL}
      external={true}
      customStyle={instructionsLinkStyle}
      label="View detailed installation instructions"
    />
    <a
      href={INSTALLATION_INSTRUCTIONS_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-hidden="true"
      tabIndex={-1}
      className="ab-test-modal__tokens__instructions-link__icon-link">
      <img src="/assets/icons/external.svg" alt="" className="ab-test-modal__tokens__instructions-link__icon" />
    </a>
  </div>
);

interface ExtensionTokenField {
  extensionId: number;
  extensionName: string;
  usageToken: string;
}

const VariantExtensionTokens: React.FC<{ variantName: string; extensionTokenFields: ExtensionTokenField[] }> = ({
  variantName,
  extensionTokenFields,
}) => (
  <>
    <div className="ab-test-modal__tokens__divider" />
    <div className="ab-test-modal__tokens__group-title">{variantName}</div>
    {extensionTokenFields.map(({ extensionId, extensionName, usageToken }) => (
      <div key={`extension-${extensionId}`} className="ab-test-modal__tokens__variant">
        <CopyableField label={extensionName} value={usageToken} />
      </div>
    ))}
  </>
);

interface TokensForShopifySectionProps {
  formData: AbTestFormData;
  extensions: ICheckoutExtensions[];
  extensionGroups: IExtensionGroup[];
  widgetType: AbTestWidgetType;
  savedAbTestId?: number;
}

export const TokensForShopifySection: React.FC<TokensForShopifySectionProps> = ({
  formData,
  extensions,
  extensionGroups,
  widgetType,
  savedAbTestId,
}) => {
  const selectedVariants = formData?.abTestVariants?.filter((variant) => variant.isSelected) || [];
  const abTestToken = savedAbTestId ? `${EXTENSIONS_AB_TEST_ID_PREFIX}${savedAbTestId}` : '';

  const buildExtensionTokenFields = (variant: TrafficAllocationVariant): ExtensionTokenField[] =>
    (variant.widgetSelections ?? [])
      .map((selection) => extensions.find((extension) => extension.id === selection.widgetExternalId))
      .filter((extension) => extension?.usageToken)
      .map((extension) => ({
        extensionId: extension.id,
        extensionName: extension.template?.internalName || 'Extension',
        usageToken: extension.usageToken,
      }));

  const getTokenForExtensionGroup = (extensionGroupExternalId: number | null): string => {
    const extensionGroup = extensionGroups.find((group) => group.id === extensionGroupExternalId);
    return extensionGroup
      ? buildExtensionsGroupUsageToken(extensionGroup.type ?? ExtensionsGroupType.EXTENSIONS, extensionGroup.id)
      : '';
  };

  if (selectedVariants.length === 0) {
    return (
      <div className="ab-test-modal__tokens">
        <p className="ab-test-modal__tokens__empty">No variants selected for this test.</p>
      </div>
    );
  }

  return (
    <div className="ab-test-modal__tokens">
      <div className="ab-test-modal__tokens__intro">
        <div className="ab-test-modal__tokens__title">Copy each token into your Shopify checkout editor.</div>
        <InstallationInstructionsLink />
      </div>
      {abTestToken && (
        <div className="ab-test-modal__tokens__test-id">
          <CopyableField label="Test ID" value={abTestToken} />
        </div>
      )}
      <div className="ab-test-modal__tokens__variants">
        {widgetType === AbTestWidgetType.EXTENSION
          ? selectedVariants.map((variant, index) => {
              const extensionTokenFields = buildExtensionTokenFields(variant);
              return extensionTokenFields.length > 0 ? (
                <VariantExtensionTokens
                  key={`variant-${index}`}
                  variantName={variant.name}
                  extensionTokenFields={extensionTokenFields}
                />
              ) : null;
            })
          : selectedVariants.map((variant, index) => {
              const token = getTokenForExtensionGroup(variant.widgetExternalId);
              return token ? (
                <div key={`variant-${index}`} className="ab-test-modal__tokens__variant">
                  <CopyableField label={variant.name} value={token} />
                </div>
              ) : null;
            })}
      </div>
    </div>
  );
};
